import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "bc_flash_dismissed_at";

const TONE_STYLES = {
    info: {
        background: "#000000",
        color: "#F2ECE0",
        borderBottom: "1px solid #D4BC7C",
        accent: "#D4BC7C",
    },
    vacances: {
        background: "#000000",
        color: "#D4BC7C",
        borderBottom: "1px solid #D4BC7C",
        accent: "#D4BC7C",
    },
    urgent: {
        background: "#D4BC7C",
        color: "#000000",
        borderBottom: "1px solid #000000",
        accent: "#000000",
    },
};

const TONE_LABEL = {
    info: "Information",
    vacances: "Vacances",
    urgent: "Urgent",
};

export default function AnnouncementBanner() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchData = () =>
            api
                .get("/announcement", { withCredentials: false })
                .then((res) => {
                    if (!cancelled) setData(res.data);
                })
                .catch(() => {
                    if (!cancelled) setData({ visible: false });
                });
        fetchData();
        const handler = () => fetchData();
        window.addEventListener("bc-announcement-updated", handler);
        return () => {
            cancelled = true;
            window.removeEventListener("bc-announcement-updated", handler);
        };
    }, []);

    useEffect(() => {
        if (!data?.visible) return;
        // Reset dismissal if a NEW updated_at is served (message was changed)
        const key = data.updated_at || "no-ts";
        const lastDismissed = sessionStorage.getItem(DISMISS_KEY);
        setDismissed(lastDismissed === key);
    }, [data]);

    if (!data || !data.visible || dismissed) return null;

    const tone = TONE_STYLES[data.tone] || TONE_STYLES.info;
    const label = TONE_LABEL[data.tone] || TONE_LABEL.info;

    const onDismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, data.updated_at || "no-ts");
        setDismissed(true);
    };

    return (
        <div
            role="status"
            aria-live="polite"
            data-testid="flash-info-banner"
            data-tone={data.tone}
            style={{
                background: tone.background,
                color: tone.color,
                borderBottom: tone.borderBottom,
                width: "100%",
                position: "relative",
                zIndex: 60,
            }}
        >
            <div
                className="max-w-7xl mx-auto flex items-start gap-3 px-4 md:px-10 py-2.5"
                style={{ minHeight: "42px" }}
            >
                <span
                    className="hidden sm:inline-flex items-center px-2 py-0.5 flex-shrink-0"
                    style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.62rem",
                        fontWeight: 600,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        border: `1px solid ${tone.accent}`,
                        color: tone.accent,
                        marginTop: "2px",
                    }}
                    data-testid="flash-info-tone"
                >
                    {label}
                </span>
                <p
                    className="flex-1 min-w-0 text-sm leading-snug"
                    style={{
                        fontFamily: "Montserrat, sans-serif",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                    }}
                    data-testid="flash-info-message"
                >
                    {data.message}
                </p>
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Fermer le bandeau"
                    data-testid="flash-info-close"
                    className="flex-shrink-0 p-1 -m-1 opacity-70 hover:opacity-100 transition-opacity"
                    style={{ color: tone.color, marginTop: "1px" }}
                >
                    <X size={16} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}
